import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { getVideoBySlug, getRelatedVideos } from '@/mocks/videos';
import { getCategoryBySlug } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoBySlug(id) : undefined;

  if (!video) {
    return <NotFound />;
  }

  const category = getCategoryBySlug(video.categorySlug);
  const relatedVideos = getRelatedVideos(video.id, video.categorySlug);
  const formatViews = (views: number) => {
    if (views >= 10000) return (views / 10000).toFixed(1) + '万';
    return views.toString();
  };

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    image: video.thumbnail,
    author: {
      '@type': 'Person',
      name: video.author,
    },
    datePublished: video.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '91视频在线观看',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91video.com/article/${video.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      <Navbar />
      <main className="pt-20">
        <article className="px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: video.category, href: `/category/${video.categorySlug}` },
                { label: video.title },
              ]}
            />

            <div className="mt-6">
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <Link
                  to={`/category/${video.categorySlug}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors whitespace-nowrap"
                >
                  {video.category}
                </Link>
                {video.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                {video.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                <span className="whitespace-nowrap flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {video.author}
                </span>
                <span className="whitespace-nowrap flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {video.publishedAt}
                </span>
                <span className="whitespace-nowrap flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(video.views)}次播放
                </span>
                <span className="whitespace-nowrap flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong>{video.rating}</strong>
                </span>
                <span className="whitespace-nowrap flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {video.duration}
                </span>
              </div>
            </div>

            <div className="mt-6 rounded-xl overflow-hidden aspect-video bg-background-200">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover"
                title={`${video.title} - 91视频在线观看`}
              />
            </div>

            <div className="mt-8 prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              [&_p]:mb-4 [&_p]:text-sm [&_p]:md:text-base [&_p]:leading-relaxed"
              dangerouslySetInnerHTML={{ __html: video.content }}
            />

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
              {video.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-lg whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <Link
                  to={`/category/${video.categorySlug}`}
                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{video.category}
                </Link>
                <div className="flex items-center gap-3">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer">
                    <i className="ri-heart-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedVideos.length > 0 && (
          <section className="px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}